import { useNavigate, useParams } from 'react-router'
import range from 'lodash/range'

import ExpandAltOutlinedIcon from '@ant-design/icons/ExpandAltOutlined'

import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import { RoleGuard } from '@shared/providers/src/MeProvider'
import { usePatient } from '@shared/providers/src/PatientProvider'
import { UserRole } from '@shared/utils'

import { PatientEncounters } from '@pages/Encounters'
import { usePatientEncounters, usePatientEncountersFilters } from '@pages/Encounters/PatientEncounters'
import PatientAccessGuard from '@components/PatientAccessGuard'
import Tile from '@components/Tile'

import { Empty, IconCircleButton } from '../ProfileTab.utils'
import { CreateEncounterButton } from './Encounters.utils'

const LIMIT = 4

export default function Encounters() {
  const { id } = useParams()
  const navigate = useNavigate()

  const patient = usePatient()
  const { data, isRefreshing, isPending } = usePatientEncounters(id, { order: 'desc', limit: LIMIT })
  const [{ encountersExpanded }, updateFilters] = usePatientEncountersFilters()

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Tile
      title="Encounters"
      refreshing={isRefreshing}
      action={
        <Stack direction="row" spacing={1}>
          <IconCircleButton disabled={(data?.length ?? 0) === 0} size="small" onClick={() => updateFilters({ encountersExpanded: true })}>
            <ExpandAltOutlinedIcon />
          </IconCircleButton>
          <RoleGuard
            prohibited={[
              UserRole.Pharmacy,
              UserRole.PharmacyManager,
              UserRole.Manager,
              UserRole.Manager340B,
              UserRole.Compliance,
              UserRole.ClinicalLHC,
              UserRole.ClinicalQI,
            ]}
          >
            <PatientAccessGuard patientId={id}>
              <CreateEncounterButton patient={patient} />
            </PatientAccessGuard>
          </RoleGuard>
        </Stack>
      }
      sx={{ height: '100%' }}
    >
      <PatientEncounters.Modal
        patientId={id}
        open={encountersExpanded}
        canDelete
        onClose={() => updateFilters({ encountersExpanded: false })}
      />
      {showLoading && <Loading />}
      {showEmpty && <Empty />}
      {showData && (
        <List disablePadding data-testid="encounters">
          {data.map((encounter, i) => (
            <Item
              key={encounter.id}
              divider={i < data.length - 1}
              encounter={encounter}
              onClick={() => navigate(`/app/patients/${id}/encounters/${encounter.id}`)}
            />
          ))}
        </List>
      )}
    </Tile>
  )
}

function Item({ divider, encounter, onClick }) {
  return (
    <ListItemButton divider={divider} onClick={onClick}>
      <Stack direction="row" sx={{ gap: 2, justifyContent: 'space-between', width: '100%' }}>
        <Typography>{encounter.description}</Typography>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-end' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            #{encounter.id}
          </Typography>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{encounter.startOrCreateInProviderTimezone}</Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

Item.Loading = function ({ divider }) {
  return (
    <ListItemButton divider={divider}>
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'space-between', width: '100%' }}>
        <Typography>
          <Skeleton width={150} />
        </Typography>
        <Typography>
          <Skeleton width={100} />
        </Typography>
      </Stack>
    </ListItemButton>
  )
}

function Loading() {
  return range(0, LIMIT).map((i) => <Item.Loading key={i} divider={i < LIMIT - 1} />)
}
